import { useState } from "react";
import {
    Boxes,
    LayoutTemplate,
    Layers,
    SlidersHorizontal,
} from "lucide-react";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import {
    useNestedGrid,
    type FillPattern,
    type GridShape,
    type LeafCellShape,
    type LeafColorMode,
    type RandomizeGridCapMode,
    type VariantConfig,
} from "./NestedGridContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clampInt(v: string | number, min: number, max: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n)
        ? Math.max(min, Math.min(max, Math.floor(n)))
        : min;
}

function clampFloat(v: string | number, min: number, max: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : min;
}

const POSITIONS = [
    "top-left",
    "top",
    "top-right",
    "left",
    "center",
    "right",
    "bottom-left",
    "bottom",
    "bottom-right",
];

// ─── Per-variant editor ───────────────────────────────────────────────────────

interface VariantEditorProps {
    lvl: number;
    vi: number;
    variant: VariantConfig;
}

function VariantEditor({ lvl, vi, variant }: VariantEditorProps) {
    const { updateVariant, updatePacking, squareGrid } = useNestedGrid();

    const upV = (
        field: keyof Omit<VariantConfig, "id" | "packing">,
        val: unknown,
    ) => {
        const patch = { [field]: val } as Partial<VariantConfig>;
        if (squareGrid) {
            if (field === "columns") patch.rows = val as number;
            if (field === "rows") patch.columns = val as number;
            if (field === "cellWidth") patch.cellHeight = val as number;
            if (field === "cellHeight") patch.cellWidth = val as number;
        }
        updateVariant(lvl, vi, patch);
    };

    const upP = (field: keyof VariantConfig["packing"], val: unknown) =>
        updatePacking(lvl, vi, { [field]: val } as Partial<
            VariantConfig["packing"]
        >);

    return (
        <div className="nested-grid-variant">
            <div className="nested-grid-section-label">Grid geometry</div>
                <div className="field-row">
                    <label className="field-label">
                        Shape
                        <select
                            value={variant.gridShape ?? "rectangular"}
                            onChange={(e) =>
                                upV("gridShape", e.target.value as GridShape)
                            }
                            className="curve-shape-select"
                        >
                            <option value="rectangular">Rectangular</option>
                            <option value="polar">Polar (columns to circle)</option>
                            <option value="spiral">Spiral (row-major path)</option>
                            <option value="hyperbolic">
                                Hyperbolic disk (Poincare)
                            </option>
                            <option value="sin">sin(z) conformal</option>
                            <option value="z2_over_2">z^2/2 conformal</option>
                        </select>
                    </label>
                </div>

                {(variant.gridShape ?? "rectangular") === "spiral" && (
                    <div className="field-row">
                        <label className="field-label">
                            Spiral turns
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={24}
                                step={0.25}
                                value={variant.spiralTurns ?? 3}
                                onChange={(v) =>
                                    upV(
                                        "spiralTurns",
                                        clampFloat(v, 0.25, 24),
                                    )
                                }
                            />
                        </label>
                    </div>
                )}

                <div className="field-row">
                    <label className="field-label">
                        Columns
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={100}
                            step={1}
                            value={variant.columns}
                            onChange={(v) => upV("columns", clampInt(v, 1, 100))}
                        />
                    </label>
                    <label className="field-label">
                        Rows
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={100}
                            step={1}
                            value={variant.rows}
                            onChange={(v) => upV("rows", clampInt(v, 1, 100))}
                        />
                    </label>
                </div>
                <div className="field-row">
                    <label className="field-label">
                        Cell Width
                        <DebouncedInput
                            type="number"
                            min={1}
                            step={1}
                            value={variant.cellWidth}
                            onChange={(v) =>
                                upV("cellWidth", clampFloat(v, 1, 9999))
                            }
                        />
                    </label>

                    <label className="field-label">
                        Cell Height
                        <DebouncedInput
                            type="number"
                            min={1}
                            step={1}
                            value={variant.cellHeight}
                            onChange={(v) =>
                                upV("cellHeight", clampFloat(v, 1, 9999))
                            }
                        />
                    </label>
                </div>
                <div className="field-row">
                    <label className="field-label">
                        Spacing H
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={variant.spacingX}
                            onChange={(v) =>
                                upV("spacingX", clampFloat(v, 0, 999))
                            }
                        />
                    </label>
                    <label className="field-label">
                        Spacing V
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={variant.spacingY}
                            onChange={(v) =>
                                upV("spacingY", clampFloat(v, 0, 999))
                            }
                        />
                    </label>
                </div>

            <div className="nested-grid-section-label">Fill & distribution</div>
                <div className="field-row">
                    <label className="field-label">
                        Density (%)
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={variant.density}
                            onChange={(v) => upV("density", clampFloat(v, 0, 100))}
                        />
                    </label>
                </div>
                <div className="field-row">
                    <label className="field-label">
                        Fill Pattern
                        <select
                            value={variant.fillPattern}
                            onChange={(e) =>
                                upV("fillPattern", e.target.value as FillPattern)
                            }
                            className="curve-shape-select"
                        >
                            <option value="even">Even</option>
                            <option value="linear-gradient">Linear Gradient</option>
                            <option value="radial-gradient">Radial Gradient</option>
                            <option value="reverse-radial-gradient">
                                Reverse Radial Gradient
                            </option>
                            <option value="perlin-noise">Perlin Noise</option>
                        </select>
                    </label>
                </div>

                {variant.fillPattern === "linear-gradient" && (
                    <>
                        <div className="field-row">
                            <label className="field-label">
                                Start Position
                                <select
                                    value={variant.startPosition}
                                    onChange={(e) =>
                                        upV("startPosition", e.target.value)
                                    }
                                    className="curve-shape-select"
                                >
                                    {POSITIONS.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                End Position
                                <select
                                    value={variant.endPosition}
                                    onChange={(e) =>
                                        upV("endPosition", e.target.value)
                                    }
                                    className="curve-shape-select"
                                >
                                    {POSITIONS.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </>
                )}

                {(variant.fillPattern === "radial-gradient" ||
                    variant.fillPattern === "reverse-radial-gradient") && (
                    <div className="field-row">
                        <label className="field-label">
                            Center Position
                            <select
                                value={variant.startPosition}
                                onChange={(e) =>
                                    upV("startPosition", e.target.value)
                                }
                                className="curve-shape-select"
                            >
                                {POSITIONS.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                )}

                <div className="field-row">
                    <label className="field-label">
                        Noise (%)
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={variant.noise}
                            onChange={(v) => upV("noise", clampFloat(v, 0, 100))}
                        />
                    </label>

                    <label className="field-label">
                        Dithering (%)
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={variant.dithering}
                            onChange={(v) =>
                                upV("dithering", clampFloat(v, 0, 100))
                            }
                        />
                    </label>
                </div>

            <div className="nested-grid-section-label">Packing</div>
                <div className="field-row">
                    <label className="field-label">
                        Max Pack Size
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={20}
                            step={1}
                            value={variant.packing.maxPackSize}
                            onChange={(v) =>
                                upP("maxPackSize", clampInt(v, 1, 20))
                            }
                        />
                    </label>

                    <label className="field-label">
                        Pack Probability (%)
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={variant.packing.probability}
                            onChange={(v) =>
                                upP("probability", clampFloat(v, 0, 100))
                            }
                        />
                    </label>
                </div>
                <div className="field-row">
                    <label className="field-label">
                        Packing Seed
                        <DebouncedInput
                            type="number"
                            step={1}
                            value={variant.packing.seed}
                            onChange={(v) => upP("seed", Math.floor(Number(v)))}
                        />
                    </label>
                </div>
                <div className="field-row">
                    <CheckboxField
                        checked={variant.packing.irregularShapes}
                        onChange={(c) => upP("irregularShapes", c)}
                        label="Irregular shapes"
                        inline
                    />
                </div>
        </div>
    );
}

// ─── Main controls ────────────────────────────────────────────────────────────

export function NestedGridControls() {
    const {
        nestingLevels,
        levelGroups,
        leafColorMode,
        leafFill,
        leafCellShape,
        leafGradientStops,
        seed,
        keepAspectRatio,
        containAspectFit,
        squareGrid,
        randomizeGridCapMode,
        randomizeGridCapBase,
        randomizeGridMinEnabled,
        randomizeGridMinValue,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
        noSpacing,
        polarSpiralEmbedRotation,
        setNestingLevels,
        setLeafColorMode,
        setLeafFill,
        setLeafCellShape,
        setLeafGradientStops,
        setSeed,
        setKeepAspectRatio,
        setContainAspectFit,
        setSquareGrid,
        setRandomizeGridCapMode,
        setRandomizeGridCapBase,
        setRandomizeGridMinEnabled,
        setRandomizeGridMinValue,
        setMirrorChildrenEnabled,
        setMirrorChildrenPercent,
        setRotateChildrenEnabled,
        setRotateChildrenPercent,
        setNoSpacing,
        setPolarSpiralEmbedRotation,
        addVariant,
        removeVariant,
        randomizeAllVariants,
        lockedVariantIds,
        toggleVariantLock,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useNestedGrid();

    const [activeLevel, setActiveLevel] = useState(0);
    const [activeVariants, setActiveVariants] = useState<number[]>([]);

    const getActiveVariant = (lvl: number) => activeVariants[lvl] ?? 0;
    const setActiveVariant = (lvl: number, vi: number) => {
        setActiveVariants((prev) => {
            const next = [...prev];
            next[lvl] = vi;
            return next;
        });
    };

    const clampedLevel = Math.min(activeLevel, nestingLevels - 1);

    const currentGroup = levelGroups[clampedLevel];
    const currentVariantIdx = Math.min(
        getActiveVariant(clampedLevel),
        (currentGroup?.variants.length ?? 1) - 1,
    );
    const currentVariant = currentGroup?.variants[currentVariantIdx];

    const levelLabel = (lvl: number) => {
        if (nestingLevels === 1) return "Level 1";
        if (lvl === 0) return `Level 1 (leaf)`;
        if (lvl === nestingLevels - 1) return `Level ${lvl + 1} (root)`;
        return `Level ${lvl + 1}`;
    };

    return (
        <div className="panel-controls nested-grid-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Nested Grid</h2>
                    <ToolDescriptionPopover dialogLabel="Nested Grid tool description">
                        <p>
                            Build a recursive grid: each nesting level can have
                            its own column/row layout, fill pattern, and packing.
                            The leaf level draws cells; deeper levels subdivide
                            parent cells. Use multiple variants per level to pick
                            layouts at random. Save and load presets from the
                            selector above.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="nested-grid"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Global & leaf" icon={SlidersHorizontal}>
                    <div className="field-row">
                        <label className="field-label">
                            Nesting Levels
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={5}
                                step={1}
                                value={nestingLevels}
                                onChange={(v) =>
                                    setNestingLevels(clampInt(v, 1, 5))
                                }
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Leaf color
                            <select
                                value={leafColorMode}
                                onChange={(e) =>
                                    setLeafColorMode(
                                        e.target.value as LeafColorMode,
                                    )
                                }
                                className="curve-shape-select"
                            >
                                <option value="solid">Solid</option>
                                <option value="gradient-by-size">
                                    Gradient by rect size
                                </option>
                            </select>
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Cell shape (leaf)
                            <select
                                value={leafCellShape}
                                onChange={(e) =>
                                    setLeafCellShape(
                                        e.target.value as LeafCellShape,
                                    )
                                }
                                className="curve-shape-select"
                            >
                                <option value="square">Square</option>
                                <option value="circle">Circle</option>
                                <option value="rounded-square">
                                    Rounded square
                                </option>
                                <option value="right-triangle">
                                    Right triangle
                                </option>
                                <option value="hollow-square">
                                    Hollow square
                                </option>
                            </select>
                        </label>
                    </div>

                    {leafColorMode === "solid" ? (
                        <div className="field-row">
                            <label className="field-label">
                                Fill color
                                <input
                                    type="color"
                                    value={leafFill}
                                    onChange={(e) =>
                                        setLeafFill(e.target.value)
                                    }
                                    className="nested-grid-color-input"
                                />
                            </label>
                        </div>
                    ) : (
                        <>
                            <div className="nested-grid-section-label">
                                Gradient stops (smallest to largest rect)
                            </div>
                            {leafGradientStops.map((stop, i) => (
                                <div
                                    className="field-row nested-grid-gradient-stop-row"
                                    key={`gradient-stop-${i}`}
                                >
                                    <label className="field-label">
                                        {i === 0
                                            ? "Smallest"
                                            : i === leafGradientStops.length - 1
                                              ? "Largest"
                                              : `Mid ${i}`}
                                        <input
                                            type="color"
                                            value={stop}
                                            onChange={(e) => {
                                                const next = [
                                                    ...leafGradientStops,
                                                ];
                                                next[i] = e.target.value;
                                                setLeafGradientStops(next);
                                            }}
                                            className="nested-grid-color-input"
                                        />
                                    </label>
                                    {leafGradientStops.length > 2 && (
                                        <button
                                            type="button"
                                            className="nested-grid-remove-btn"
                                            title="Remove stop"
                                            onClick={() =>
                                                setLeafGradientStops((prev) =>
                                                    prev.length <= 2
                                                        ? prev
                                                        : prev.filter(
                                                              (_, j) => j !== i,
                                                          ),
                                                )
                                            }
                                        >
                                            x
                                        </button>
                                    )}
                                </div>
                            ))}
                            <div className="field-row">
                                <button
                                    type="button"
                                    className="nested-grid-add-btn"
                                    onClick={() =>
                                        setLeafGradientStops((prev) => [
                                            ...prev,
                                            prev[prev.length - 1] ?? "#888888",
                                        ])
                                    }
                                >
                                    Add color stop
                                </button>
                            </div>
                        </>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Global Seed
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={seed}
                                onChange={(v) =>
                                    setSeed(Math.floor(Number(v)) || 0)
                                }
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Layout & output" icon={LayoutTemplate}>
                    <div className="field-row">
                        <CheckboxField
                            checked={keepAspectRatio}
                            onChange={setKeepAspectRatio}
                            label="Keep aspect ratio (no stretch)"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={containAspectFit}
                            onChange={setContainAspectFit}
                            label="Fit inside cell (contain, no overflow)"
                            disabled={!keepAspectRatio}
                            className={
                                !keepAspectRatio
                                    ? "nested-grid-option--muted"
                                    : undefined
                            }
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={squareGrid}
                            onChange={setSquareGrid}
                            label="Square grid (rows = columns, cell H = W)"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={noSpacing}
                            onChange={setNoSpacing}
                            label="No spacing (output only)"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Child grids" icon={Boxes}>
                    <div className="field-row">
                        <CheckboxField
                            checked={mirrorChildrenEnabled}
                            onChange={setMirrorChildrenEnabled}
                            label="Mirror child grids"
                            inline
                        />
                    </div>
                    {mirrorChildrenEnabled && (
                        <div className="field-row">
                            <label className="field-label">
                                Mirrored (% of children)
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={mirrorChildrenPercent}
                                    onChange={(v) =>
                                        setMirrorChildrenPercent(
                                            clampFloat(v, 0, 100),
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row">
                        <CheckboxField
                            checked={rotateChildrenEnabled}
                            onChange={setRotateChildrenEnabled}
                            label="Rotate child grids (90 deg steps)"
                            inline
                        />
                    </div>
                    {rotateChildrenEnabled && (
                        <div className="field-row">
                            <label className="field-label">
                                Rotated (% of children)
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={rotateChildrenPercent}
                                    onChange={(v) =>
                                        setRotateChildrenPercent(
                                            clampFloat(v, 0, 100),
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row">
                        <CheckboxField
                            checked={polarSpiralEmbedRotation}
                            onChange={setPolarSpiralEmbedRotation}
                            label="Polar/spiral/hyperbolic: rotate nested grids"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Levels, variant & randomization"
                    icon={Layers}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Random grid size cap
                            <select
                                value={randomizeGridCapMode}
                                onChange={(e) =>
                                    setRandomizeGridCapMode(
                                        e.target.value as RandomizeGridCapMode,
                                    )
                                }
                                className="curve-shape-select"
                            >
                                <option value="none">None (max 24)</option>
                                <option value="global">Global</option>
                                <option value="by-level">
                                    By nesting level (Nx(level+1))
                                </option>
                                <option value="exponential">
                                    Exponential (N^(level+1), cap 500)
                                </option>
                            </select>
                        </label>
                    </div>

                    {randomizeGridCapMode !== "none" && (
                        <div className="field-row">
                            <label className="field-label">
                                {randomizeGridCapMode === "global"
                                    ? "Max rows & columns (randomize)"
                                    : randomizeGridCapMode === "by-level"
                                      ? "Base N (level L max = Nx(L+1))"
                                      : "Base N (level L max = min(500, N^(L+1)))"}
                                <DebouncedInput
                                    type="number"
                                    min={2}
                                    step={1}
                                    value={randomizeGridCapBase}
                                    onChange={(v) => {
                                        const n = clampInt(v, 2, 9999);
                                        setRandomizeGridCapBase(n);
                                    }}
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row">
                        <CheckboxField
                            checked={randomizeGridMinEnabled}
                            onChange={setRandomizeGridMinEnabled}
                            label="Min grid size (randomize only)"
                            inline
                        />
                    </div>
                    {randomizeGridMinEnabled && (
                        <div className="field-row">
                            <label className="field-label">
                                Min rows & columns (each level)
                                <DebouncedInput
                                    type="number"
                                    min={2}
                                    step={1}
                                    value={randomizeGridMinValue}
                                    onChange={(v) => {
                                        const n = clampInt(v, 2, 9999);
                                        setRandomizeGridMinValue(n);
                                    }}
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row nested-grid-randomize-row">
                        <button
                            type="button"
                            className="nested-grid-lock-btn"
                            disabled={!currentVariant}
                            aria-pressed={
                                currentVariant
                                    ? lockedVariantIds.has(currentVariant.id)
                                    : false
                            }
                            title={
                                currentVariant &&
                                lockedVariantIds.has(currentVariant.id)
                                    ? "This variant will not change when you randomize"
                                    : "Skip this variant when randomizing"
                            }
                            onClick={() => {
                                if (currentVariant)
                                    toggleVariantLock(currentVariant.id);
                            }}
                        >
                            {currentVariant &&
                            lockedVariantIds.has(currentVariant.id)
                                ? "Unlock variant"
                                : "Lock variant"}
                        </button>
                        <button
                            type="button"
                            className="nested-grid-randomize-btn"
                            onClick={() => randomizeAllVariants()}
                        >
                            Randomize all variants
                        </button>
                    </div>

                    <div className="nested-grid-tabs">
                        {Array.from({ length: nestingLevels }, (_, i) => (
                            <button
                                key={i}
                                type="button"
                                className={`nested-grid-tab${clampedLevel === i ? " nested-grid-tab--active" : ""}`}
                                onClick={() => setActiveLevel(i)}
                            >
                                {levelLabel(i)}
                            </button>
                        ))}
                    </div>

                    <div className="nested-grid-variant-actions">
                        <button
                            type="button"
                            className="nested-grid-add-btn"
                            onClick={() => {
                                addVariant(clampedLevel);
                                setActiveVariant(
                                    clampedLevel,
                                    currentGroup.variants.length,
                                );
                            }}
                        >
                            + Add variant
                        </button>
                        {currentGroup.variants.length > 1 && (
                            <button
                                type="button"
                                className="nested-grid-remove-btn"
                                onClick={() =>
                                    removeVariant(
                                        clampedLevel,
                                        currentVariantIdx,
                                    )
                                }
                            >
                                Remove variant
                            </button>
                        )}
                    </div>

                    {currentGroup && currentGroup.variants.length > 1 && (
                        <div className="nested-grid-tabs nested-grid-tabs--variant">
                            {currentGroup.variants.map((v, vi) => (
                                <button
                                    key={v.id}
                                    type="button"
                                    className={`nested-grid-tab nested-grid-tab--sm${currentVariantIdx === vi ? " nested-grid-tab--active" : ""}${lockedVariantIds.has(v.id) ? " nested-grid-tab--locked" : ""}`}
                                    onClick={() =>
                                        setActiveVariant(clampedLevel, vi)
                                    }
                                >
                                    V{vi + 1}
                                </button>
                            ))}
                        </div>
                    )}

                    {currentVariant && (
                        <VariantEditor
                            key={`${clampedLevel}-${currentVariantIdx}`}
                            lvl={clampedLevel}
                            vi={currentVariantIdx}
                            variant={currentVariant}
                        />
                    )}
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
